/**
 * Art library: loads the TexturePacker JSON-Hash atlases listed in
 * public/generated/atlas/index.json and resolves asset ids to texture frames. Any id not in
 * an atlas gets a procedural fallback texture (fallbackArt.ts) the first time it is used.
 */
import type * as Phaser from 'phaser';
import { cities } from '../config/content.js';
import { PIP_ACTIONS, pipFrameCount, renderFallback } from './fallbackArt.js';

export interface FrameRef {
  texture: string;
  frame: string | undefined;
}

interface SheetRef {
  key: string;
  json: string;
  image?: string;
  group?: string;
}

const isCb = (s: SheetRef) => /_cb(_\d+)?$/.test(s.group ?? s.key);
/** Groups the core scenes use (everything else belongs to a feature). */
const REST = /^(items_|shop_|facades_|customers_|env_|pip_|ui$)/;

/** Groups another city owns (by city id or its shop ids) and night variants are skipped. */
function forCity(group: string, city: string): boolean {
  if (group.includes('night')) return false;
  for (const c of cities) {
    if (c.id === city) continue;
    if (group.includes(`_${c.id}`) || c.shops.some((shop) => group.includes(shop))) return false;
  }
  return true;
}

interface AtlasJson {
  frames: Record<string, unknown> | Array<{ filename: string }>;
  meta?: { image?: string };
}

function str(o: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const k of keys) if (typeof o[k] === 'string') return o[k] as string;
  return undefined;
}

/** Accepts the index shapes the art build may emit: string list, {sheets:[…]}, or a name→file map. */
export function normalizeIndex(raw: unknown): SheetRef[] {
  const list: unknown[] = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && Array.isArray((raw as { sheets?: unknown }).sheets)
      ? (raw as { sheets: unknown[] }).sheets
      : raw && typeof raw === 'object'
        ? Object.entries(raw as Record<string, unknown>).map(([k, v]) => (typeof v === 'string' ? { name: k, json: v } : { name: k, ...(v as object) }))
        : [];
  const out: SheetRef[] = [];
  for (const e of list) {
    if (typeof e === 'string') {
      out.push({ key: e.replace(/\.json$/, '').split('/').pop()!, json: e.endsWith('.json') ? e : `${e}.json` });
      continue;
    }
    if (!e || typeof e !== 'object') continue;
    const o = e as Record<string, unknown>;
    const name = str(o, 'name', 'key', 'id', 'sheet');
    const json = str(o, 'json', 'data', 'atlas', 'file') ?? (name ? `${name}.json` : undefined);
    if (!json) continue;
    const image = str(o, 'image', 'texture', 'webp', 'png');
    const group = str(o, 'group');
    out.push({ key: name ?? json.replace(/\.json$/, '').split('/').pop()!, json, ...(image ? { image } : {}), ...(group ? { group } : {}) });
  }
  return out;
}

function frameNames(data: AtlasJson): string[] {
  return Array.isArray(data.frames) ? data.frames.map((f) => f.filename) : Object.keys(data.frames);
}

const stripExt = (n: string) => n.replace(/\.(png|webp|jpg)$/i, '');

export class Art {
  private frames = new Map<string, FrameRef>();
  private sheets: SheetRef[] = [];
  private loadedSheets = new Set<string>();
  /** texture key → image URL (DOM thumbnails reuse the atlas image) */
  private sheetUrls = new Map<string, string>();
  private dataUrls = new Map<string, string>();
  private indexUrl = '';
  /** groups the art build marks as first-load (loaded before the first scene) */
  private firstLoad: string[] = [];
  private rest = new Map<string, Promise<void>>();
  /** animation ids the art build knows about (e.g. chr_pip_act_worried), loaded or not */
  private knownAnims = new Set<string>();
  colorBlind = false;
  /** sheets that failed to load (reported, not fatal) */
  readonly errors: string[] = [];
  private loadedListeners = new Set<(group: string) => void>();
  private groupLoads = new Map<string, Promise<void>>();
  /** groups whose download is running (asked for through ensureGroup; neither in nor failed yet) */
  private downloading = new Set<string>();

  constructor(private tm: () => Phaser.Textures.TextureManager) {}

  private get textures(): Phaser.Textures.TextureManager {
    return this.tm();
  }

  /** Fetch index.json; resolves to [] when the art build has not run (fallback art only). */
  async loadIndex(url: string): Promise<SheetRef[]> {
    this.indexUrl = new URL(url, document.baseURI).href;
    const res = await fetch(this.indexUrl).catch(() => null);
    if (!res || !res.ok || !(res.headers.get('content-type') ?? '').includes('json')) return [];
    const raw: unknown = await res.json();
    this.sheets = normalizeIndex(raw);
    const anims = (raw as { anims?: unknown }).anims;
    if (anims && typeof anims === 'object') for (const k of Object.keys(anims)) this.knownAnims.add(k);
    const fl = (raw as { firstLoad?: unknown }).firstLoad;
    this.firstLoad = Array.isArray(fl) ? fl.filter((g): g is string => typeof g === 'string') : [];
    return this.sheets;
  }

  /** First-load sheets (or all when the index names none); colour-blind sheets only when enabled. */
  loadFirst(onProgress: (p: number) => void): Promise<void> {
    const first = this.firstLoad.length ? this.sheets.filter((s) => this.firstLoad.includes(s.group ?? s.key)) : this.sheets;
    return this.loadSheets(first, onProgress);
  }

  /**
   * Everything else the city's scenes need (shops, facades, customers, extra Pip actions);
   * scenes await it. Feature sheets (rare, league, store, stickers, share cards…) are not
   * part of it: features load their own group with ensureGroup().
   */
  loadRest(city: string): Promise<void> {
    let p = this.rest.get(city);
    if (!p) {
      p = this.loadSheets(this.sheets.filter((s) => REST.test(s.group ?? s.key) && forCity(s.group ?? s.key, city)), () => {});
      this.rest.set(city, p);
    }
    return p;
  }

  /** Load one group now (e.g. the city's customers before the first round). */
  ensureGroup(group: string): Promise<void> {
    let p = this.groupLoads.get(group);
    if (!p) {
      const sheets = this.sheets.filter((s) => (s.group ?? s.key) === group);
      if (!sheets.length) return Promise.resolve(); // unknown (yet): fallback art, nothing to remember
      // colour-blind sheets of the group come later through setColorBlind when switched on
      this.downloading.add(group);
      // announced when it settles, a failed download too: whatever waited for it can stop waiting
      p = this.loadSheets(sheets, () => {}).finally(() => {
        this.downloading.delete(group);
        this.emitLoaded(group);
      });
      this.groupLoads.set(group, p);
    }
    return p;
  }

  /** Group names in the index matching the patterns (`stickers_*`, `customers_*`, exact names). */
  groupsMatching(patterns: readonly string[]): string[] {
    const all = [...new Set(this.sheets.map((s) => s.group ?? s.key))];
    const out = new Set<string>();
    for (const p of patterns) {
      if (!p.includes('*')) {
        if (all.includes(p)) out.add(p);
        continue;
      }
      const re = new RegExp(`^${p.split('*').map((x) => x.replace(/[.+?^${}()|[\]\\]/g, '\\$&')).join('.*')}$`);
      for (const g of all) if (re.test(g)) out.add(g);
    }
    return [...out];
  }

  /**
   * Lazy feature art (`league`, `store`, `rare`, `stickers_*`, `teams`, `evt_harvest`,
   * `pip_extra`, `customers_*`, city-pack groups): starts loading in the background and
   * resolves when every group is in. Until then DOM sprites show the fallback and re-render
   * when the group arrives (onLoaded); canvas scenes should await the promise.
   */
  loadArtGroups(groups: readonly string[]): Promise<void> {
    return Promise.all(this.groupsMatching(groups).map((g) => this.ensureGroup(g))).then(() => undefined);
  }

  /** True once the group has been asked for (ensureGroup): each of its sheets is fetched, failed or on its way. */
  groupLoaded(group: string): boolean {
    const sheets = this.sheets.filter((s) => (s.group ?? s.key) === group && (this.colorBlind || !isCb(s)));
    return sheets.length > 0 && sheets.every((s) => this.loadedSheets.has(s.key)) && this.groupLoads.has(group);
  }

  /**
   * True while the group's download is running: art that can still arrive. Unlike groupLoaded it is
   * false before the group is asked for, and again once the download has settled (in or failed).
   */
  groupLoading(group: string): boolean {
    return this.downloading.has(group);
  }

  /** Called with the group name each time a group finishes loading, or fails to (DOM sprites refresh). */
  onLoaded(fn: (group: string) => void): () => void {
    this.loadedListeners.add(fn);
    return () => this.loadedListeners.delete(fn);
  }

  private emitLoaded(group: string): void {
    for (const fn of this.loadedListeners) fn(group);
  }

  /**
   * The groups a round of this level needs: its items (the city's `items_<city>` sheet, an
   * event's `evt_<id>` sheet, or a city pack's) and the customers it names (`cus_<city>_…`).
   */
  levelGroups(level: { city: string; customers?: ReadonlyArray<{ id: string }> }): string[] {
    const city = level.city;
    const items = this.groupsMatching([`items_${city}`, `items_${city}_cb`, `evt_${city}`]);
    const customerCities = new Set((level.customers ?? []).map((c) => /^cus_([a-z]+)_/.exec(c.id)?.[1]).filter((c): c is string => !!c));
    if (!customerCities.size) customerCities.add(city);
    const customers = this.groupsMatching([...customerCities].map((c) => `customers_${c}`));
    return [...items, ...customers];
  }

  /**
   * Register another atlas index (a downloaded city pack's art): its sheets join the library
   * under their groups and load on demand like the built-in ones.
   */
  async addIndex(url: string): Promise<string[]> {
    const abs = new URL(url, document.baseURI).href;
    const res = await fetch(abs).catch(() => null);
    if (!res || !res.ok) return [];
    const added = normalizeIndex(await res.json()).map((s) => ({ ...s, json: new URL(s.json, abs).href, ...(s.image ? { image: new URL(s.image, abs).href } : {}) }));
    const known = new Set(this.sheets.map((s) => s.key));
    this.sheets.push(...added.filter((s) => !known.has(s.key)));
    return [...new Set(added.map((s) => s.group ?? s.key))];
  }

  private async loadSheets(list: SheetRef[], onProgress: (p: number) => void): Promise<void> {
    const wanted = list.filter((s) => this.colorBlind || !isCb(s));
    let done = 0;
    await Promise.all(
      wanted.map((s) =>
        this.loadSheet(s).then(() => onProgress(++done / Math.max(1, wanted.length))),
      ),
    );
  }

  async setColorBlind(on: boolean): Promise<void> {
    this.colorBlind = on;
    if (on) await Promise.all(this.sheets.filter(isCb).map((s) => this.loadSheet(s)));
  }

  private async loadSheet(s: SheetRef): Promise<void> {
    if (this.loadedSheets.has(s.key)) return;
    this.loadedSheets.add(s.key);
    const jsonUrl = new URL(s.json, this.indexUrl).href;
    const res = await fetch(jsonUrl).catch(() => null);
    if (!res || !res.ok) {
      this.errors.push(s.key);
      return;
    }
    const data = (await res.json()) as AtlasJson;
    // per-frame pivots would override every sprite origin the views set: drop them
    for (const f of Array.isArray(data.frames) ? data.frames : Object.values(data.frames)) delete (f as { pivot?: unknown }).pivot;
    const imageUrl = new URL(s.image ?? data.meta?.image ?? s.json.replace(/\.json$/, '.webp'), jsonUrl).href;
    const img = new Image();
    img.decoding = 'async';
    img.src = imageUrl;
    const ok = await img.decode().then(
      () => true,
      () => false,
    );
    if (!ok) {
      this.errors.push(s.key);
      return;
    }
    const texKey = `atlas:${s.key}`;
    if (!this.textures.exists(texKey)) this.textures.addAtlas(texKey, img, data as unknown as object);
    this.sheetUrls.set(texKey, imageUrl);
    for (const name of frameNames(data)) {
      const id = stripExt(name);
      const prev = this.frames.get(id);
      // a real frame replaces a fallback generated before this sheet arrived
      if (!prev || prev.texture.startsWith('fb:')) this.frames.set(id, { texture: texKey, frame: name });
    }
  }

  /** True when a real (atlas) frame exists for the id. */
  has(key: string): boolean {
    return this.frames.has(key);
  }

  /** Resolve an asset id to a texture frame, generating the fallback on first use. */
  get(key: string): FrameRef {
    const f = this.frames.get(key);
    if (f) return f;
    const texKey = `fb:${key}`;
    if (!this.textures.exists(texKey)) this.textures.addCanvas(texKey, renderFallback(key));
    const ref = { texture: texKey, frame: undefined };
    this.frames.set(key, ref);
    return ref;
  }

  /** Item frame honouring the colour-blind pattern setting (`_cb` variants). */
  item(id: string): FrameRef {
    if (this.colorBlind) return this.get(`${id}_cb`);
    return this.get(id);
  }

  /** Frames of a Pip action (`chr_pip_act_{action}_{nn}`), from atlases or fallback. */
  /** Frames of a Pip action; null while its sheet is still downloading (a stand-in plays). */
  pipFrames(action: string): FrameRef[] | null {
    const prefix = `chr_pip_act_${action}_`;
    const names = [...this.frames.keys()].filter((k) => k.startsWith(prefix) && this.frames.get(k)!.texture.startsWith('atlas:')).sort();
    if (names.length) return names.map((n) => this.frames.get(n)!);
    if (this.knownAnims.has(`chr_pip_act_${action}`)) return null;
    const n = pipFrameCount(action);
    return Array.from({ length: n }, (_, i) => this.get(`${prefix}${String(i).padStart(2, '0')}`));
  }

  /** Register Pip animations at 12 fps; call again after more sheets load to add the rest. */
  createPipAnims(anims: Phaser.Animations.AnimationManager): void {
    for (const action of PIP_ACTIONS) {
      const key = `pip_${action}`;
      if (anims.exists(key)) continue;
      const frames = this.pipFrames(action);
      if (!frames) continue;
      anims.create({
        key,
        frames: frames.map((f) => ({ key: f.texture, frame: f.frame })),
        frameRate: 12,
        repeat: action === 'idle' || action === 'worried' || action === 'panic' || action === 'sleep' ? -1 : 0,
      });
    }
  }

  /**
   * CSS background for showing an asset in the DOM overlay (shop style thumbnails…):
   * the atlas image cropped by background-position, or the fallback canvas as a data URL.
   */
  cssSprite(key: string, box: number): Record<string, string> {
    const ref = this.get(key);
    const tex = this.textures.get(ref.texture);
    const frame = tex.get(ref.frame);
    let url = this.sheetUrls.get(ref.texture);
    if (!url) {
      url = this.dataUrls.get(ref.texture);
      if (!url) {
        url = (tex.getSourceImage() as HTMLCanvasElement).toDataURL();
        this.dataUrls.set(ref.texture, url);
      }
    }
    const src = tex.getSourceImage() as { width: number; height: number };
    const k = box / Math.max(frame.cutWidth, frame.cutHeight);
    const w = frame.cutWidth * k;
    const h = frame.cutHeight * k;
    return {
      width: `${w}px`,
      height: `${h}px`,
      backgroundImage: `url("${url}")`,
      backgroundSize: `${src.width * k}px ${src.height * k}px`,
      backgroundPosition: `${-frame.cutX * k}px ${-frame.cutY * k}px`,
      backgroundRepeat: 'no-repeat',
    };
  }

  /**
   * Canvas source of a frame (share cards draw atlas art into a 2D canvas). `ox/oy` and
   * `fullW/fullH` undo atlas trimming so callers can place it in its untrimmed box.
   */
  frameSource(key: string): { img: CanvasImageSource; x: number; y: number; w: number; h: number; ox: number; oy: number; fullW: number; fullH: number; real: boolean } {
    const ref = this.get(key);
    const tex = this.textures.get(ref.texture);
    const f = tex.get(ref.frame);
    return {
      img: tex.getSourceImage() as CanvasImageSource,
      x: f.cutX,
      y: f.cutY,
      w: f.cutWidth,
      h: f.cutHeight,
      ox: f.x,
      oy: f.y,
      fullW: f.realWidth,
      fullH: f.realHeight,
      real: ref.texture.startsWith('atlas:'),
    };
  }

  /** Clear fallback caches (e.g. after atlases arrive late) so ids re-resolve. */
  dropFallbacks(): void {
    for (const [k, v] of this.frames) if (v.texture.startsWith('fb:')) this.frames.delete(k);
  }
}
